import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Box, Stack } from "@mui/material";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { useUsers } from "../../../../../utils/reactQuery/users";
import { assignUser } from "../../../../../sevices/apiBoards";
import useToastPromise from "../../../../../hooks/useToast";
import { addBoardUser } from "../../../../../schemas/addBoardUser";
import { filterUserOptions } from "./userOptionsHelper";
import UserSelect from "./UserSelect";
import RoleSelect from "./RoleSelect";
import FormActions from "./FormActions";
import useBoardIdByKey from "../../../../../hooks/useBoardIdByKey";
import { RHFCheckbox } from "../../../../../components/RFH";
import Avatar from "../../../../../components/Avatar";
import { hasImage } from "../../../../../utils";

function Form({
  onClose,
  boardUsers,
  isMobile,
  isColabrotar,
  handleAddColabrotor,
  setOpen,
  assigneeUserId,
  currentUser,
  isUpdateUser,
}) {
  const { id } = useBoardIdByKey();
  const queryClient = useQueryClient();
  const { isLoading: isUserLoading, users, isError: isUserError } = useUsers();
  const [userOption, setUserOption] = useState([]);
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const [newBoardUser, setNewBoardUser] = useState([]);
  const { showToastPromise } = useToastPromise();
  const [isSelectedRole, setIsSelectedRole] = useState("User");

  const roles = [
    { label: "Board Admin", value: "Board Admin" },
    { label: "User", value: "User" },
  ];

  console.log("currentUser==> ", currentUser);
  useEffect(() => {
    if (!isColabrotar) {
      if (!isUserLoading && !isUserError) {
        const filteredArray = users.data.data.responseData
          .filter((el) => el.active)
          .sort((a, b) => a.first_name.localeCompare(b.first_name));

        setUserOption(
          filteredArray.map((el) => ({
            label: `${el.first_name} ${el.last_name} (${el.email})`,
            value: el.user_id,
          }))
        );
      }
      setIsSelectedRole(currentUser?.is_admin ? "Board Admin" : "User");
    } else {
      setNewBoardUser(
        boardUsers
          ?.filter((el) => el.user_id !== assigneeUserId)
          ?.map((el) => ({
            label: el.username,
            value: el.user_id,
          }))
      );
    }
  }, [
    users,
    isUserLoading,
    boardUsers,
    currentUser,
    isColabrotar,
    assigneeUserId,
  ]);

  // const {
  //   handleSubmit,
  //   control,
  //   setValue,
  //   ...methods
  // } = useForm({
  //   defaultValues: {
  //     board_id: id,
  //     user_id: currentUser?.user_id,
  //     user_role: isSelectedRole,
  //   },
  //   resolver: yupResolver(addBoardUser),
  // });

  // React Hook Form
  const {
    register,
    handleSubmit,
    formState,
    control,
    setValue,
    reset,
    watch,
    ...methods
  } = useForm({
    defaultValues: {
      board_id: id,
      user_id: currentUser?.user_id,
      user_role: isSelectedRole,

      ...(isColabrotar
        ? {}
        : {
            can_create_task:
              isSelectedRole == "Board Admin" ||
              currentUser?.can_create_task ||
              !isUpdateUser,
            can_edit_task:
              isSelectedRole == "Board Admin" ||
              currentUser?.can_edit_task ||
              !isUpdateUser,
            can_delete_card:
              isSelectedRole == "Board Admin" || currentUser?.can_delete_card,
            can_reassign_user:
              isSelectedRole == "Board Admin" ||
              currentUser?.can_reassign_user ||
              !isUpdateUser,
            can_work_before_estimation:
              isSelectedRole == "Board Admin" ||
              currentUser?.can_work_before_estimation ||
              !isUpdateUser,
            can_update_status:
              isSelectedRole == "Board Admin" ||
              currentUser?.can_update_status ||
              !isUpdateUser,
            can_add_work_log:
              isSelectedRole == "Board Admin" ||
              currentUser?.can_add_work_log ||
              !isUpdateUser,
            can_edit_work_log:
              isSelectedRole == "Board Admin" ||
              currentUser?.can_edit_work_log ||
              !isUpdateUser,
            can_delete_work_log:
              isSelectedRole == "Board Admin" ||
              currentUser?.can_delete_work_log,
            can_move_edit_work_log:
              isSelectedRole == "Board Admin" ||
              currentUser?.can_move_edit_work_log,
            can_add_user: isSelectedRole == "Board Admin",
            can_change_milestone: isSelectedRole == "Board Admin",
          }),
    },
    resolver: yupResolver(addBoardUser),
  });

  useEffect(() => {
    if (!isColabrotar) {
      const newValues = {
        board_id: id,
        user_role: isSelectedRole,

        can_create_task:
          isSelectedRole === "Board Admin" ||
          currentUser?.can_create_task ||
          !isUpdateUser,
        can_edit_task:
          isSelectedRole === "Board Admin" ||
          currentUser?.can_edit_task ||
          !isUpdateUser,
        can_delete_card:
          isSelectedRole === "Board Admin" || currentUser?.can_delete_card,
        can_reassign_user:
          isSelectedRole === "Board Admin" ||
          currentUser?.can_reassign_user ||
          !isUpdateUser,
        can_work_before_estimation:
          isSelectedRole === "Board Admin" ||
          currentUser?.can_work_before_estimation ||
          !isUpdateUser,
        can_update_status:
          isSelectedRole === "Board Admin" ||
          currentUser?.can_update_status ||
          !isUpdateUser,
        can_add_work_log:
          isSelectedRole === "Board Admin" ||
          currentUser?.can_add_work_log ||
          !isUpdateUser,
        can_edit_work_log:
          isSelectedRole === "Board Admin" ||
          currentUser?.can_edit_work_log ||
          !isUpdateUser,
        can_delete_work_log:
          isSelectedRole === "Board Admin" || currentUser?.can_delete_work_log,
        can_move_edit_work_log:
          isSelectedRole === "Board Admin" ||
          currentUser?.can_move_edit_work_log,
        can_add_user: isSelectedRole === "Board Admin",
        can_change_milestone: isSelectedRole === "Board Admin",
      };

      Object.keys(newValues).forEach((key) => {
        setValue(key, newValues[key]);
      });
    }
  }, [isSelectedRole, setValue, id, currentUser, isColabrotar, isUpdateUser]);

  useEffect(() => {
    setValue("board_id", id);
    setValue("user_role", isSelectedRole);
  }, [isSelectedRole, setValue, id]);

  const { mutate: assignUserMutate } = useMutation({
    mutationFn: async (data) => await showToastPromise(assignUser, data),
    onSuccess: () => {
      const queries = [
        [`boardUsers`, id],
        [`boardName`, id],
        [`boards`],
        ["boardUser", currentUser?.user_id],
      ];
      queries.forEach((query) =>
        queryClient.invalidateQueries({ queryKey: query })
      );
      setLoading(false);
      onClose();
    },
    onError: (error) => {
      console.log(error);
      setLoading(false);
    },
  });

  const submit = (data) => {
    console.log("FormData==> ", data);
    setApiError("");
    setLoading(true);
    if (!isColabrotar) {
      assignUserMutate(data);
    } else {
      handleAddColabrotor(data?.user_id);
      setOpen(false);
    }
  };

  const updatedUserOptions = filterUserOptions(
    userOption,
    boardUsers,
    currentUser
  );

  console.log(
    "is edit ===== ",
    boardUsers.find((user) => user?.user_id == currentUser?.user_id),
    currentUser
  );

  const currentUserProfile = boardUsers.find(
    (user) => user?.user_id == currentUser?.user_id
  );

  return (
    <FormProvider control={control} {...methods}>
      <Box
        component="form"
        onSubmit={handleSubmit(submit)}
        noValidate
        sx={{ padding: isMobile ? "8px 5px !important" : "0px" }}
        className="add-board-user-form"
      >
        <Stack spacing={3}>
          {apiError && <Alert severity="error">{apiError}</Alert>}

          {isUpdateUser ? (
            <div className="user-permission-head">
              <Avatar
                title={`${currentUserProfile.username}`}
                user_image={currentUserProfile?.user_image}
                className="user-avatar"
                style={
                  !hasImage(currentUserProfile?.user_image) && {
                    bgcolor: currentUserProfile?.profile_color?.background,
                    color: currentUserProfile?.profile_color?.text,
                  }
                }
              >
                {currentUserProfile?.username}
              </Avatar>
              <p className="size-16 font-bold">
                {currentUserProfile?.username}
              </p>
            </div>
          ) : (
            <UserSelect
              isColabrotar={isColabrotar}
              newBoardUser={newBoardUser}
              updatedUserOptions={updatedUserOptions}
             
            />
          )}

          {!isColabrotar && (
            <>
             {(!isUpdateUser || currentUser) && <RoleSelect
                isMobile={isMobile}
                isColabrotar={isColabrotar}
                roles={roles}
                onRoleChange={setIsSelectedRole}
                currentUser={currentUser}
              />}

              <p className="size-18 weight-600 text-color border-bottom pb-2">
                Permission
              </p>
              <p className="weight-600 size-15 mb-2">My Tasks</p>
              <div className="d-flex w-100 justify-content-space-between flex-wrap">
                <RHFCheckbox
                  name={"can_create_task"}
                  label={"Create"}
                  className={"checkbox-permission"}
                  disabled={isSelectedRole == "Board Admin"}
                />
                <RHFCheckbox
                  name={"can_edit_task"}
                  label={"Edit"}
                  className={"checkbox-permission"}
                  disabled={isSelectedRole == "Board Admin"}
                />
                <RHFCheckbox
                  name={"can_delete_card"}
                  label={"Delete"}
                  className={"checkbox-permission"}
                  disabled={isSelectedRole == "Board Admin"}
                />
                <RHFCheckbox
                  name={"can_reassign_user"}
                  label={"Reassign tasks assigned to others"}
                  className={"checkbox-permission"}
                  disabled={isSelectedRole == "Board Admin"}
                />
                <RHFCheckbox
                  name={"can_work_before_estimation"}
                  label={"Start work before estimation"}
                  className={"checkbox-permission"}
                  disabled={isSelectedRole == "Board Admin"}
                />
                <RHFCheckbox
                  name={"can_update_status"}
                  label={"Update Status"}
                  className={"checkbox-permission"}
                  disabled={isSelectedRole == "Board Admin"}
                />
              </div>

              <p className="weight-600 size-15 mb-2">Worklogs</p>
              <div className="d-flex w-100 justify-content-space-between flex-wrap">
                <RHFCheckbox
                  name={"can_add_work_log"}
                  label={"Add"}
                  className={"checkbox-permission"}
                  disabled={isSelectedRole == "Board Admin"}
                />
                <RHFCheckbox
                  name={"can_edit_work_log"}
                  label={"Edit"}
                  className={"checkbox-permission"}
                  disabled={isSelectedRole == "Board Admin"}
                />
                <RHFCheckbox
                  name={"can_delete_work_log"}
                  label={"Delete"}
                  className={"checkbox-permission"}
                  disabled={isSelectedRole == "Board Admin"}
                />
                <RHFCheckbox
                  name={"can_move_edit_work_log"}
                  label={"Move to a different ticket"}
                  className={"checkbox-permission"}
                  disabled={isSelectedRole == "Board Admin"}
                />
              </div>

              {/* <p className="weight-600 size-15">Others</p>
              <div className="d-flex w-50">
                <RHFCheckbox
                  name={"can_add_user"}
                  label={"Add User"}
                  disabled={true}
                />
                <RHFCheckbox
                  name={"can_change_milestone"}
                  label={"Milestones"}
                  disabled={true}
                />
              </div> */}
            </>
          )}

          <FormActions
            loading={loading}
            isUpdateUser={isUpdateUser}
            onClose={onClose}
          />
        </Stack>
      </Box>
    </FormProvider>
  );
}

export default Form;
